// Ingredient-quantity scaling.
//
// Ported from the source recipe collection this data came from
// (https://pru-lilylau.github.io/home-tool/recipes.html). Quantities are
// written for ~4.5 people and scaled at render time for the selected bucket.
//
// Only ingredient quantities scale. Step text is prose and is left exactly as
// written — a regex confident enough to rewrite "600克水" would also rewrite
// "燉2小時", turning a cooking time into nonsense. The UI shows a note instead
// whenever the selected serving differs from the base.

export const BASE_SERVING = 4.5;

// Units that come in whole discrete pieces (cloves, eggs, wings, slices…) —
// these scale to whole numbers only, never half-pieces like "1.5個".
const COUNT_UNITS = [
    "個", "隻", "瓣", "片", "條", "塊", "粒", "包", "棵", "支", "份", "盒",
    "罐", "尾", "對", "磚", "枚", "串", "顆", "件",
    "buah", "siung", "potong", "lembar", "butir", "batang", "kotak", "ekor",
    "piraso", "hiwa", "kahon", "tangkay", "dahon",
];

function isCountUnitAt(str, index) {
    const rest = str.slice(index).trimStart();
    return COUNT_UNITS.some((u) => rest.startsWith(u));
}

// Round by magnitude rather than blindly, so 600克 × 1.33 reads as 800克 and
// not 799.8克. Counts land on whole pieces with a floor of one.
function scaleNum(value, ratio, isCount) {
    const x = value * ratio;
    let r;
    if (isCount) r = Math.round(x);
    else if (x < 10) r = Math.round(x * 2) / 2;
    else if (x < 50) r = Math.round(x);
    else if (x < 200) r = Math.round(x / 5) * 5;
    else r = Math.round(x / 25) * 25;
    const floor = isCount ? 1 : 0.5;
    return r < floor ? floor : r;
}

function formatNum(n, sep) {
    let s = Number.isInteger(n) ? String(n) : n.toFixed(1);
    if (sep === ",") s = s.replace(".", ",");
    return s;
}

/**
 * Scale one quantity string, e.g. "600克", "1,5 sdm", "半個", "1/2 buah", "2-3隻".
 * `lang` picks the decimal convention — Indonesian writes 1,5 where zh/fil write 1.5.
 */
export function scaleQty(str, ratio, lang) {
    if (!str || ratio === 1) return str;
    const sep = lang === "id" ? "," : ".";

    // "半個" — Chinese half.
    if (lang === "zh" && str.startsWith("半")) {
        const rest = str.slice(1);
        const scaled = scaleNum(0.5, ratio, isCountUnitAt(rest, 0));
        return (scaled === 0.5 ? "半" : formatNum(scaled, sep)) + rest;
    }

    // "1/2 buah" — written fraction.
    const halfMatch = str.match(/^1\/2(\s)/);
    if (halfMatch) {
        const rest = str.slice(halfMatch[0].length);
        const scaled = scaleNum(0.5, ratio, isCountUnitAt(rest, 0));
        return (scaled === 0.5 ? "1/2 " : formatNum(scaled, sep) + " ") + rest;
    }

    // "2-3隻" — a range scales at both ends, and collapses if they meet.
    const rangeRegex = lang === "id"
        ? /(\d+(?:,\d+)?)-(\d+(?:,\d+)?)/
        : /(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/;
    const rangeMatch = str.match(rangeRegex);
    if (rangeMatch) {
        const isCount = isCountUnitAt(str, rangeMatch.index + rangeMatch[0].length);
        const a = scaleNum(parseFloat(rangeMatch[1].replace(",", ".")), ratio, isCount);
        const b = scaleNum(parseFloat(rangeMatch[2].replace(",", ".")), ratio, isCount);
        const repl = a === b ? formatNum(a, sep) : formatNum(a, sep) + "-" + formatNum(b, sep);
        return str.replace(rangeRegex, repl);
    }

    const numRegex = lang === "id" ? /\d+(?:,\d+)?/g : /\d+(?:\.\d+)?/g;
    return str.replace(numRegex, (m, offset) => {
        const val = parseFloat(lang === "id" ? m.replace(",", ".") : m);
        const isCount = isCountUnitAt(str, offset + m.length);
        return formatNum(scaleNum(val, ratio, isCount), sep);
    });
}

/** Multiplier for a serving bucket's midpoint against how the recipes are written. */
export function ratioFor(serving) {
    if (!serving || typeof serving.mid !== "number") return 1;
    return serving.mid / BASE_SERVING;
}
